import React, {useMemo} from 'react';
import {Alert, Button, Chip, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import TableHeader from "../layout/table/TableHeader";
import {useGetJourneeQuery} from "../../../modules/simulations/simulations.api";
import SimulationJourneeMap from "./SimulationJourneeMap";
import {formatHeuresDepuisMinutes, journeeATrajet, libelleEquipage, visitesOrdonnees} from "./simulationRules";

/**
 * Bloc « Journée » d'un scénario : visites ordonnées de chaque équipe (+ carte de la journée). Partagé par la page
 * scénario et l'atelier (sans carte). `onFiger(nomenclature, date)` : « Figer ce jour » (absent = pas de bouton).
 */
const HEAD_VISITES = [
    {id: 'ordre', label: '#'},
    {id: 'pav', label: 'PAV'},
    {id: 'flux', label: 'Flux'},
    {id: 'bacs', label: 'Bacs'},
    {id: 'dispo', label: 'Disponible'},
    {id: 'arrivee', label: 'Arrivée'},
    {id: 'attente', label: 'Attente'},
    {id: 'raison', label: 'Raison'},
];

function BlocJournee({simulation, date, onFiger, avecCarte = true}) {
    const {data, isLoading, error} = useGetJourneeQuery({id: simulation.id, date});
    const base = simulation.parametres?.base;
    const depot = simulation.parametres?.depot;

    const equipes = useMemo(() => (data?.journees ?? []).map((j) => j.equipe), [data]);
    const trajets = useMemo(() => Object.fromEntries((data?.journees ?? []).map((j) => [j.equipe, journeeATrajet(j)])), [data]);

    if (isLoading) return <Skeleton variant="rectangular" height={300} sx={{mt: 2}}/>;
    if (error) return <Alert severity="error" sx={{mt: 2}}>Impossible de charger la journée du {date}.</Alert>;

    return (
        <div className="sectionContainer">
            <Typography variant="h5" className="sectionHeader">Journée du {date}</Typography>
            <Grid container spacing={3}>
                <Grid size={{xs: 12, md: avecCarte ? 7 : 12}}>
                    {data.journees.map((j) => (
                        <div key={j.equipe} style={{marginBottom: '1.5rem'}}>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                Équipe {j.equipe} · {libelleEquipage(j.equipage)} · {j.bacs} bacs · {formatHeuresDepuisMinutes(j.minutesTravail)}
                            </Typography>
                            {j.libelleTrajet && <Typography variant="body2">Trajet : {j.libelleTrajet}</Typography>}
                            {j.raisonEquipage && <Typography variant="caption" sx={{display: 'block'}}>{j.raisonEquipage}</Typography>}
                            <TableContainer component={Paper} variant="outlined" sx={{mt: 1}}>
                                <Table size="small" aria-label={`visites équipe ${j.equipe}`}>
                                    <TableHeader headCells={HEAD_VISITES}/>
                                    <TableBody>
                                        {visitesOrdonnees(j).map((v) => (
                                            <TableRow key={`${v.ordre}-${v.nomenclature}`}>
                                                <TableCell>{v.ordre}</TableCell>
                                                <TableCell>
                                                    {v.nomenclature}
                                                    {v.partie && <Chip label={`stock ${v.partie}`} size="small" sx={{ml: 1}}/>}
                                                    {v.fige && <Chip label="figé" size="small" sx={{ml: 1}}/>}
                                                </TableCell>
                                                <TableCell>{[].concat(v.flux ?? []).join(', ')}</TableCell>
                                                <TableCell>{v.quantite}</TableCell>
                                                <TableCell>{v.heureDisponible ?? '—'}</TableCell>
                                                <TableCell>{v.arrivee}</TableCell>
                                                <TableCell>{v.attenteMin > 0 ? `${v.attenteMin} min` : '—'}</TableCell>
                                                <TableCell>{v.raison}</TableCell>
                                                <TableCell>
                                                    {onFiger &&
                                                        <Button color="primary" size="small" onClick={() => onFiger(v.nomenclature, date)}>
                                                            Figer ce jour
                                                        </Button>}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ))}
                </Grid>
                {avecCarte &&
                    <Grid size={{xs: 12, md: 5}}>
                        <SimulationJourneeMap points={data.points} equipes={equipes} trajets={trajets} base={base} depot={depot}/>
                    </Grid>}
            </Grid>
        </div>
    );
}

export default BlocJournee;
